/**
 * Taking a fan out of the loyalty programme: the order the two writes happen in, and what a failure
 * of each one costs.
 *
 * The mirror image of ./useJoinFunnel.js's submit(), and deliberately the same SHAPE rather than the
 * same sequence reversed:
 *
 *     join   setLoyaltyConsentGranted( true ).then( () => client.joinLoyalty( … ) )
 *     leave  withdrawLoyaltyConsent().then( () => withdrawal.raise( … ) ).then( confirm )
 *
 * In both, our own record is written first and the processor is told inside that write's `.then`, so
 * there is no flag anybody can forget to check -- the ordering is a property of the code. And in
 * both the record can be AHEAD of the processor and never behind, which is the direction that
 * matters on a consent surface.
 *
 * ---
 *
 * **ONLY THE CONSENT WRITE MAY FAIL THE FLOW**, and that is the load-bearing decision in this file.
 *
 * The obvious alternative -- ask 3Halves first, so nothing is recorded here until the processor has
 * agreed -- reads as the careful one and is not. Two reasons, and the second is decisive:
 *
 * 1. **It would buy nothing.** A raised request is a row in the middleware's collection. Nothing
 *    acts on it: the executor that archives, drops and erases is admin-only and nothing schedules
 *    it. So the call does not stop the fan's data being processed either, and ordering it first
 *    protects nothing while costing a compensating cancel and a whole extra failure mode.
 * 2. **It would make leaving depend on a third party.** A blocking 3Halves leg fails whenever the
 *    gateway is down, slow or refusing -- so withdrawing consent would be harder than granting it,
 *    which is the one asymmetry a consent surface may never have. (This used to cite every deployed
 *    origin but dev failing the preflight; the gateway admits them all since 2026-09-30, and the
 *    rule never rested on it. D-097.)
 *
 * After the consent write lands the fan has left in every sense this repository can guarantee: the
 * record says so, ./consentGate.js refuses every fan-scoped call before the wire, and the score
 * tracker stops collecting (D-071). What follows is best effort, and its failure is recorded rather
 * than shown -- which is only defensible because the authored copy never promised an immediate
 * deletion. See App\Loyalty\MembershipLabels.
 *
 * ---
 *
 * Everything is injected, so leaveLoyalty.test.js drives every branch with no network, no storage
 * and no clock. The one import is a pure field reader, and it is imported rather than inlined so
 * that the wire's `requestId` is spelled in exactly one place in the repository.
 */

import { requestIdOf } from './withdrawal.js';
import { withDeadline as withAbortableDeadline } from './deadline.js';

/** What a whole attempt came to. Published so a support conversation can start from a fact. */
export const LEAVE_OUTCOME = {
	/**
	 * The processor confirmed the withdrawal. On the legacy path that means both writes landed; on
	 * the journal path our own record is the journal row, and the Auth0 write may still be the
	 * server's to finish -- retried from cron while the gate already reads "not granted" (driven
	 * with Auth0 unreachable on 2026-09-25). The fan has left either way.
	 */
	OK: 'ok',
	/** We recorded it; the processor has no record of this fan to withdraw from yet. */
	OK_NOT_PROVISIONED: 'ok-not-provisioned',
	/**
	 * We recorded it; the request did not reach the processor. The fan has still left.
	 *
	 * Since the membership journal (2026-09-25) this is no longer the end of it: the transaction
	 * stays open and its 3Halves leg is resumed on the fan's later pages, paced by the server, until
	 * it lands or the leave's deadline flags it for a manual withdrawal. Only the legacy path --
	 * a page cached before the journal's routes, a table not installed yet -- still means "lost".
	 */
	INCOMPLETE: 'incomplete',
	/** Nothing was recorded anywhere. The fan is still in the programme. */
	CONSENT_WRITE_FAILED: 'consent-write-failed',
};

/**
 * Where the outcome waits out the redirect.
 *
 * **It has to survive a navigation, which is the whole reason it is not just an attribute.** The
 * flow ends in `location.replace()`, so anything written to `<html>` or to the console at the moment
 * of leaving is destroyed a tick later -- and `incomplete` is exactly the branch somebody would want
 * to read afterwards. The landing page picks this up and republishes it.
 *
 * Fan-scoped by NAME, so an account switch cannot be served somebody else's answer, and read-once so
 * a stale outcome cannot reappear on a later visit.
 */
const OUTCOME_KEY = 'urc.loyalty.withdrawalOutcome';

/**
 * @param {string} fan The fan's Auth0 `sub`.
 * @return {string} That fan's key.
 */
function outcomeKey( fan ) {
	return `${ OUTCOME_KEY }.${ fan }`;
}

/**
 * Record what the attempt came to, for the page the fan is about to land on.
 *
 * @param {string} fan     The fan's Auth0 `sub`.
 * @param {string} outcome A LEAVE_OUTCOME.
 * @return {void}
 */
export function rememberLeaveOutcome( fan, outcome ) {
	if ( typeof fan !== 'string' || fan === '' ) {
		return;
	}

	try {
		window.sessionStorage.setItem( outcomeKey( fan ), outcome );
	} catch ( error ) {
		// A diagnostic that cannot be stored is not worth a second thought, let alone a throw on
		// the one path a fan must never see fail.
	}
}

/**
 * Read the outcome once and drop it.
 *
 * Read-ONCE on purpose: this answers "what happened on the way to this page", so leaving it behind
 * would have it answer the same for every later page of the browse, long after it stopped being
 * about anything.
 *
 * @param {string} fan The fan's Auth0 `sub`.
 * @return {string} The outcome, or ''.
 */
export function takeLeaveOutcome( fan ) {
	if ( typeof fan !== 'string' || fan === '' ) {
		return '';
	}

	try {
		const key = outcomeKey( fan );
		const value = window.sessionStorage.getItem( key );

		if ( value ) {
			window.sessionStorage.removeItem( key );
		}

		return value || '';
	} catch ( error ) {
		return '';
	}
}

/**
 * Build the leave sequence over its collaborators.
 *
 * @param {Object}   options                 Wiring.
 * @param {Function} options.withdrawConsent `() => Promise<void>` — the one blocking write.
 * @param {Object}   options.withdrawal      A ./withdrawal.js client.
 * @param {boolean}  options.eraseData       Whether to ask for Art. 17 erasure as well.
 * @param {Function} options.forget          `( fan ) => void` — drop the cached enrolment verdict.
 * @param {Function} options.remember        `( fan, outcome ) => void` — persist across the redirect.
 * @param {Function} [options.warn]          Logger for a leg that did not land.
 * @param {Function} [options.deadline]      `( promise, ms ) => promise` — bounds the blocking write.
 * @param {number}   [options.deadlineMs]    How long the fan may be held.
 * @param {Object}   [options.membership]    ./membership/membership.js. When it is available, the
 *                                           leave is a journal transaction; otherwise the legacy
 *                                           sequence below runs exactly as before.
 * @param {number}   [options.legMs]         How long the dialog waits on the 3Halves leg before
 *                                           leaving it to be resumed.
 * @return {(fan: string) => Promise<{outcome: string, error: *}>} The sequence. Never rejects.
 */
export function createLeaveLoyalty( {
	withdrawConsent,
	withdrawal,
	eraseData,
	forget,
	remember,
	warn = () => {},
	deadline = withDeadline,
	deadlineMs = 20000,
	membership = null,
	legMs = 10000,
} ) {
	/**
	 * Raise the request, and confirm it unless there is nothing to confirm.
	 *
	 * @return {Promise<string>} A LEAVE_OUTCOME for the best-effort half.
	 */
	function tellTheProcessor() {
		return withdrawal.raise( { withdrawProgramme: true, eraseData } ).then( raised => {
			if ( raised.outcome === 'not-provisioned' ) {
				// Nothing at the processor to withdraw from. Provisioning is asynchronous, so a fan
				// who signed up recently is legitimately here, and confirming a request that does
				// not exist would be the only thing that could turn this into an error.
				return LEAVE_OUTCOME.OK_NOT_PROVISIONED;
			}

			if ( raised.outcome !== 'ok' ) {
				warn( 'the withdrawal request did not reach the processor', raised );

				return LEAVE_OUTCOME.INCOMPLETE;
			}

			noteSubstitution( raised.body );

			// **The id from THIS answer, not from anywhere else.** Asking twice hands back the
			// existing request, so the id we are holding is whatever the middleware considers the
			// fan's open one -- which is exactly what the confirm has to name.
			return confirmOnce( requestIdOf( raised.body ) ).then( confirmed => {
				if ( confirmed.outcome === 'ok' ) {
					return LEAVE_OUTCOME.OK;
				}

				warn( 'the withdrawal request was raised but not confirmed', confirmed );

				return LEAVE_OUTCOME.INCOMPLETE;
			} );
		} );
	}

	/**
	 * Confirm, and on a 409 raise again once before trying a second time.
	 *
	 * A 409 means the request is not in a state that can be confirmed -- which happens when the one
	 * we are holding is not the one the processor now has. Asking again returns whatever is current
	 * (a second POST hands back the existing request rather than creating one), so the second
	 * confirm has something to act on. Once, and then it is an incomplete: a loop here would be a
	 * loop against a processor that has already disagreed with us twice.
	 *
	 * **The repair confirms the SECOND raise's id**, and that is the whole of what makes it a
	 * repair. Re-sending the first one would ask the processor to act on the very request it has
	 * just said it cannot -- the same call again, with a raise in front of it for decoration.
	 *
	 * @param {string} requestId The id from the raise that preceded this.
	 * @return {Promise<Object>} The confirm's result.
	 */
	function confirmOnce( requestId ) {
		return withdrawal.confirm( requestId ).then( first => {
			if ( first.outcome !== 'conflict' ) {
				return first;
			}

			return withdrawal
				.raise( { withdrawProgramme: true, eraseData } )
				.then( raised => withdrawal.confirm( requestIdOf( raised.body ) ) );
		} );
	}

	/**
	 * Say so when the request we got back is not the one we asked for.
	 *
	 * Asking twice returns the EXISTING request rather than creating a second, which is what makes
	 * a retry free -- and also means a request opened by something else can silently stand in for
	 * ours. The middleware opens one itself when it sees the opt-in claim go from granted to
	 * withdrawn on a fan's next token, and that one carries `eraseData: false`. So a fan can leave,
	 * be told the erasure was requested, and have a request standing that never asked for it.
	 *
	 * Not an error and not shown to the fan: the request exists and the fan has left. It is the kind
	 * of thing that is obvious in a log and impossible to work out afterwards without one.
	 *
	 * **It compared the wrong level of the response until 2026-09-22 and could never fire**, because
	 * the middleware answers inside a `{success, data}` envelope and nothing here unwrapped it. The
	 * unwrapping is ./withdrawal.js's now. `source` is read on trust: it is not on a fan-raised
	 * request as measured on dev that day, so the `eraseData` comparison is the whole of the guard
	 * unless the middleware's own consent-transition path files one.
	 *
	 * @param {Object|null} body What the processor returned.
	 * @return {void}
	 */
	function noteSubstitution( body ) {
		if ( ! body ) {
			return;
		}

		if ( body.eraseData !== undefined && body.eraseData !== eraseData ) {
			warn( 'the processor returned an existing request with a different eraseData', {
				asked: eraseData,
				returned: body.eraseData,
				source: body.source,
			} );
		}
	}

	/**
	 * The leave as a journal transaction.
	 *
	 * The begin is the "our record first" step now: once it answers, the server has a durable row
	 * that closes this fan's consent gate site-wide, and it drives the Auth0 write itself --
	 * inline, then from cron for as long as Auth0 needs. The 3Halves leg follows at once (withdrawal
	 * calls are exempt from the consent rule) and is given `legMs`; if it does not land, the fan has
	 * still left, and a later page resumes it.
	 *
	 * @param {string} fan The fan's Auth0 `sub`.
	 * @return {Promise<{outcome: string, error: *}>} Never rejects.
	 */
	async function viaJournal( fan ) {
		const begun = await deadline(
			Promise.resolve().then( () => membership.begin( 'leave' ) ),
			deadlineMs
		).catch( error => ( { ok: false, fallback: false, reason: 'timeout', error } ) );

		if ( begun.fallback ) {
			return legacy( fan );
		}

		if ( ! begun.ok ) {
			// Nothing the server has confirmed. A begin that landed after we stopped waiting is a
			// leave the fan asked for, and it completes on its own; the fan is told this attempt
			// did not, and pressing Leave again reuses the same open transaction.
			warn( 'the leave was not recorded', begun );
			remember( fan, LEAVE_OUTCOME.CONSENT_WRITE_FAILED );

			return {
				outcome: LEAVE_OUTCOME.CONSENT_WRITE_FAILED,
				error: begun.error || new Error( `membership begin: ${ begun.reason }` ),
			};
		}

		forget( fan );

		const summary = begun.membership;
		let outcome = summary.state === 'committed' ? LEAVE_OUTCOME.OK : LEAVE_OUTCOME.INCOMPLETE;

		if ( summary.task ) {
			const out = await withAbortableDeadline(
				signal => membership.run( summary, { wait: true, signal } ),
				legMs
			).catch( () => null );

			if ( out && ! out.skipped ) {
				await Promise.resolve( membership.report( summary.txn, out ) ).catch( () => null );

				if ( out.result === 'done' ) {
					outcome =
						out.code === 'not_provisioned'
							? LEAVE_OUTCOME.OK_NOT_PROVISIONED
							: LEAVE_OUTCOME.OK;
				} else {
					warn(
						'the withdrawal has not reached the processor yet; it will be resumed',
						out
					);
				}
			}
		}

		remember( fan, outcome );

		return { outcome, error: null };
	}

	/**
	 * @param {string} fan The fan's Auth0 `sub`.
	 * @return {Promise<{outcome: string, error: *}>} Never rejects.
	 */
	return function leave( fan ) {
		if ( membership && membership.available() ) {
			return viaJournal( fan ).catch( error => {
				warn( 'the leave failed unexpectedly', error );
				remember( fan, LEAVE_OUTCOME.CONSENT_WRITE_FAILED );

				return { outcome: LEAVE_OUTCOME.CONSENT_WRITE_FAILED, error };
			} );
		}

		return legacy( fan );
	};

	/**
	 * The sequence before the journal, and still the one a page without its routes runs.
	 *
	 * @param {string} fan The fan's Auth0 `sub`.
	 * @return {Promise<{outcome: string, error: *}>} Never rejects.
	 */
	function legacy( fan ) {
		return deadline( Promise.resolve().then( withdrawConsent ), deadlineMs ).then(
			() => {
				// From here the fan HAS left, whatever follows. Dropping the cached enrolment
				// verdict is part of that rather than cleanup after it: the entry outlives this
				// document, and a tab still holding it would answer "a member" for this fan on the
				// next page whenever the consent read is unavailable rather than denied.
				forget( fan );

				return tellTheProcessor().then( outcome => {
					remember( fan, outcome );

					return { outcome, error: null };
				} );
			},
			error => {
				// Nothing was recorded, so nothing else may run and the fan must be told. This is
				// the only branch that leaves them in the programme.
				warn( 'the consent withdrawal was not recorded', error );
				remember( fan, LEAVE_OUTCOME.CONSENT_WRITE_FAILED );

				return { outcome: LEAVE_OUTCOME.CONSENT_WRITE_FAILED, error };
			}
		);
	}
}

/**
 * Bound a promise, because the dialog it is driving cannot be dismissed while it runs.
 *
 * Every way out of `LeaveLoyaltyModal` is disabled for the length of the write -- the X, Escape and
 * the backdrop all route to an `onClose` that does nothing, so the fan cannot dismiss a write and
 * never learn whether it landed. That is right, and it is only right with a deadline behind it:
 * without one, a request that is accepted and never answered leaves them in a modal with no exit.
 *
 * The consent provider has no `AbortController`, so this does not cancel the request -- it stops
 * WAITING on it. A write that lands afterwards is not lost: it is a confirmed write, and the next
 * read of this fan's consent picks it up.
 *
 * @param {Promise} promise What to bound.
 * @param {number}  ms      How long to allow.
 * @return {Promise} The same promise, or a rejection once the time is up.
 */
function withDeadline( promise, ms ) {
	if ( typeof setTimeout !== 'function' || ! ms ) {
		return promise;
	}

	let timer = null;

	const expiry = new Promise( ( resolve, reject ) => {
		timer = setTimeout(
			() => reject( new Error( 'The loyalty consent write did not answer in time.' ) ),
			ms
		);
	} );

	return Promise.race( [ promise, expiry ] ).finally( () => {
		if ( timer !== null ) {
			clearTimeout( timer );
		}
	} );
}
